import { Link } from 'react-router-dom';
import { PageHeader } from '../components/layout/PageHeader';

export function PrivacyPage() {
  return (
    <>
      <PageHeader
        title="Privacy Policy"
        subtitle="Last updated: 23 May 2026"
      />

      <article className="page-pad pb-8">
        <div className="mx-auto max-w-3xl">
          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">Introduction</p>
            <p className="text-[0.8125rem] leading-relaxed text-body">
              DIH is committed to protecting the privacy and security of data
              processed through the Document Organisation system. This Privacy
              Policy describes the types of information we collect, how we use and
              safeguard it, and your rights regarding that information.
            </p>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">1. Information We Collect</p>
            <div className="space-y-3 text-[0.8125rem] leading-relaxed">
              <div>
                <p className="font-semibold text-petrol mb-1">Identity & Access Data</p>
                <p className="text-muted-slate">
                  When you sign in via Azure Active Directory, we receive your name,
                  email address, organisational role, and directory group memberships.
                  This information is used solely for authentication and
                  authorisation.
                </p>
              </div>
              <div>
                <p className="font-semibold text-petrol mb-1">Audit & Activity Data</p>
                <p className="text-muted-slate">
                  Every action you perform within the System — including document
                  triage decisions, merge operations, register edits, and lifecycle
                  transitions — is recorded in a tamper-evident audit log with your
                  identity and a timestamp.
                </p>
              </div>
              <div>
                <p className="font-semibold text-petrol mb-1">Document Metadata</p>
                <p className="text-muted-slate">
                  The System processes metadata associated with inbound documents,
                  including file names, sender information, dates, and extracted
                  content used for routing classification.
                </p>
              </div>
              <div>
                <p className="font-semibold text-petrol mb-1">Technical Data</p>
                <p className="text-muted-slate">
                  We collect standard technical data such as browser type, IP
                  address, and access timestamps for security monitoring and
                  performance diagnostics via Azure Application Insights.
                </p>
              </div>
            </div>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">2. How We Use Your Information</p>
            <ul className="list-none space-y-2 text-[0.8125rem] leading-relaxed text-muted-slate">
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                <span>
                  <strong className="text-body">Authentication:</strong> Verifying
                  your identity and determining your access level within the System.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                <span>
                  <strong className="text-body">Audit Compliance:</strong> Maintaining
                  a complete, immutable record of all system operations for regulatory
                  and internal compliance requirements.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                <span>
                  <strong className="text-body">Document Routing:</strong> Processing
                  document metadata through rule-based and AI-assisted classification
                  to route documents to the correct SharePoint location.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                <span>
                  <strong className="text-body">System Improvement:</strong> Analysing
                  usage patterns and routing accuracy to improve classification
                  confidence and system reliability.
                </span>
              </li>
            </ul>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">3. AI & Automated Processing</p>
            <p className="text-[0.8125rem] leading-relaxed text-body mb-2">
              The System employs large language models (LLMs) via Azure AI Foundry
              for document classification when rule-based routing cannot determine a
              destination with sufficient confidence.
            </p>
            <ul className="list-none space-y-2 text-[0.8125rem] leading-relaxed text-muted-slate">
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-teal" />
                LLM classification operates on document metadata and extracted text
                only — not on user personal data.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-teal" />
                All LLM outputs are subject to confidence thresholds. Low-confidence
                results are routed to the triage queue for human review.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-teal" />
                Document content is not used to train or fine-tune any AI models.
                Data sent to Azure AI Foundry is processed under DIH's enterprise
                agreement and is not retained by the AI service.
              </li>
            </ul>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">4. Data Storage & Security</p>
            <ul className="list-none space-y-2 text-[0.8125rem] leading-relaxed text-muted-slate">
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                All data is stored in Azure-hosted infrastructure with encryption at
                rest (AES-256) and in transit (TLS 1.2+).
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                Secrets and connection strings are managed through Azure Key Vault
                with RBAC access controls.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                Authentication is federated through Azure Active Directory using
                OIDC — no passwords are stored by the System.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-petrol" />
                Application telemetry and diagnostics are captured via Azure
                Application Insights with data residency in the configured Azure
                region.
              </li>
            </ul>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">5. Data Sharing & Third Parties</p>
            <p className="text-[0.8125rem] leading-relaxed text-body mb-2">
              We do not sell, rent, or share your personal data with third parties
              for marketing purposes. Data may be shared in the following limited
              circumstances:
            </p>
            <ul className="list-none space-y-2 text-[0.8125rem] leading-relaxed text-muted-slate">
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-3" />
                <span>
                  <strong className="text-body">Azure Services:</strong> Microsoft
                  Azure infrastructure services that host and operate the System,
                  governed by Microsoft's enterprise data processing agreements.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-3" />
                <span>
                  <strong className="text-body">Legal Obligations:</strong> When
                  required by law, regulation, or valid legal process.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-3" />
                <span>
                  <strong className="text-body">Internal Operations:</strong> With
                  other DIH systems (such as the Contact Graph module) via secured
                  API endpoints for operational purposes.
                </span>
              </li>
            </ul>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">6. Data Retention</p>
            <p className="text-[0.8125rem] leading-relaxed text-body">
              Audit log records are retained for the duration required by applicable
              regulatory obligations and internal compliance policies. Document
              metadata is retained as long as the corresponding document exists in
              the SharePoint taxonomy. When a user's access is revoked, their
              identity data is removed from active use, though historical audit
              entries are preserved for compliance integrity.
            </p>
          </section>

          <section className="brand-card mb-4">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">7. Your Rights</p>
            <p className="text-[0.8125rem] leading-relaxed text-body mb-2">
              Depending on your jurisdiction, you may have the right to:
            </p>
            <ul className="list-none space-y-2 text-[0.8125rem] leading-relaxed text-muted-slate">
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-4" />
                Request access to the personal data we hold about you.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-4" />
                Request correction of inaccurate or incomplete personal data.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-4" />
                Request deletion of your personal data, subject to legal and
                compliance retention requirements.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-4" />
                Object to automated decision-making where it produces legal or
                similarly significant effects.
              </li>
            </ul>
            <p className="mt-2 text-[0.8125rem] leading-relaxed text-body">
              To exercise any of these rights, contact your IT administrator or the
              DIH data protection team.
            </p>
          </section>

          <section className="brand-card">
            <div className="brand-card-bar bg-petrol" />
            <p className="brand-section-label mb-3">8. Changes to This Policy</p>
            <p className="text-[0.8125rem] leading-relaxed text-body">
              We may update this Privacy Policy from time to time to reflect changes
              in our practices or legal requirements. Material changes will be
              communicated through the System. We encourage you to review this page
              periodically.
            </p>
          </section>

          <div className="brand-note mt-6">
            For questions regarding this Privacy Policy or to exercise your data
            rights, please contact your IT administrator. See also our{' '}
            <Link to="/terms" className="brand-link-btn">
              Terms & Conditions
            </Link>
            .
          </div>
        </div>
      </article>
    </>
  );
}
